/**
 * Authentication Service
 * Handles Keycloak authentication and user profile management
 */

import { buildUrl, getStorageKeys, shouldRefreshToken } from '../config/services.config';
import { getConfig, getKeycloakUrl } from '../config/config.loader';
import { loginRateLimiter, sanitizeInput } from '../utils/security.utils';
import { organizationService, Organization } from './organization.service';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  refresh_expires_in: number;
  scope?: string;
}

export interface UserProfile {
  // Core Identity
  id: string;
  username: string;
  email: string;
  
  // Personal Information
  firstName: string;
  lastName: string;
  name: string;
  phone?: string;
  
  // Job Information
  jobTitle?: string;
  department?: string;
  hireDate?: string;
  employeeId?: string;
  
  // Organization Relationship
  organizationId?: string;
  organization?: Organization;
  
  // Access Control
  roles: string[];
  permissions: string[];
  status?: string;
  
  // Emergency Contact
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  
  // Profile Settings
  profilePictureUrl?: string;
  timezone?: string;
  language?: string;
  
  // Activity Tracking
  lastLogin?: string;
  loginCount?: number;
  projectCount?: number;
  
  // System Metadata
  isActive?: boolean;
  emailVerified?: boolean;
  twoFactorEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface AuthError {
  error: string;
  error_description?: string;
  message: string;
}

class AuthService {
  private tokenExpiryTime: number | null = null;
  private refreshTokenExpiryTime: number | null = null;

  constructor() {
    // Restore token expiry times from localStorage on initialization
    this.restoreExpiryTimes();
  }

  /**
   * Restore token expiry times from localStorage
   */
  private restoreExpiryTimes(): void {
    const keys = getStorageKeys();
    const storedAccessExpiry = localStorage.getItem(`${keys.accessToken}_expiry`);
    const storedRefreshExpiry = localStorage.getItem(`${keys.refreshToken}_expiry`);

    if (storedAccessExpiry) {
      this.tokenExpiryTime = parseFloat(storedAccessExpiry);
    }
    if (storedRefreshExpiry) {
      this.refreshTokenExpiryTime = parseFloat(storedRefreshExpiry);
    }
  }

  /**
   * Authenticate user with Keycloak
   */
  async login(credentials: LoginCredentials): Promise<{ tokens: AuthTokens; user: UserProfile }> {
    try {
      // Rate limiting check
      const clientId = this.getClientIdentifier();
      if (!loginRateLimiter.isAllowed(clientId, 5, 300000)) { // 5 attempts per 5 minutes
        throw {
          error: 'too_many_requests',
          message: 'Too many login attempts. Please wait before trying again.'
        };
      }

      // Sanitize input
      const sanitizedCredentials = {
        username: sanitizeInput(credentials.username),
        password: credentials.password // Don't sanitize password as it might contain special chars
      };

      // Step 1: Authenticate with Keycloak
      const tokens = await this.authenticateWithKeycloak(sanitizedCredentials);
      
      // Reset rate limiter on successful login
      loginRateLimiter.reset(clientId);
      
      // Step 2: Store tokens securely
      this.storeTokens(tokens);
      
      // Step 3: Fetch user profile from backend
      const userProfile = await this.fetchUserProfile();
      
      return { tokens, user: userProfile };
    } catch (error) {
      console.error('Login failed:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Authenticate with Keycloak using password grant
   */
  private async authenticateWithKeycloak(credentials: LoginCredentials): Promise<AuthTokens> {
    const config = getConfig();
    const keycloakTokenUrl = getKeycloakUrl(`/realms/${config.keycloak.realm}/protocol/openid-connect/token`);
    
    const formData = new URLSearchParams({
      grant_type: 'password',
      client_id: config.keycloak.clientId,
      username: credentials.username,
      password: credentials.password,
      scope: 'openid profile email'
    });

    const response = await fetch(keycloakTokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        error: errorData.error || 'authentication_failed',
        error_description: errorData.error_description || 'Invalid credentials',
        message: 'Authentication failed. Please check your username and password.'
      };
    }

    const tokens: AuthTokens = await response.json();
    
    // Calculate token expiry times
    const now = Date.now() / 1000;
    this.tokenExpiryTime = now + tokens.expires_in;
    this.refreshTokenExpiryTime = now + tokens.refresh_expires_in;
    
    return tokens;
  }

  /**
   * Fetch user profile from backend application
   */
  private async fetchUserProfile(): Promise<UserProfile> {
    const profileUrl = buildUrl('backend', '/api/v1/users/me');
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch(profileUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token might be expired, try to refresh
        await this.refreshTokens();
        return this.fetchUserProfile(); // Retry once
      }
      
      throw new Error(`Failed to fetch user profile: ${response.status}`);
    }

    const profileData = await response.json();

    // Debug logging to understand profile structure (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('User profile data:', profileData);
    }

    // Fetch full organization details if user has an organization
    let organizationDetails: Organization | undefined;
    
    // Check for organization_id (new format) or nested organization object (legacy)
    const orgId = profileData.organization_id || profileData.organization?.id;
    
    if (orgId) {
      try {
        organizationDetails = await organizationService.getOrganization(
          orgId,
          accessToken
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetched organization details:', organizationDetails);
        }
      } catch (error) {
        console.warn('Failed to fetch organization details:', error);
        // Fallback to basic organization info from profile if available
        if (profileData.organization) {
          organizationDetails = {
            id: profileData.organization.id,
            name: profileData.organization.name
          };
        } else {
          // Create minimal organization object with just ID
          organizationDetails = {
            id: orgId,
            name: 'Organization'
          };
        }
      }
    }
    
    // Transform backend response to UserProfile format
    return {
      // Core Identity
      id: profileData.user_id || profileData.id,
      username: profileData.username,
      email: profileData.email,
      
      // Personal Information
      firstName: profileData.first_name,
      lastName: profileData.last_name,
      name: profileData.full_name || `${profileData.first_name} ${profileData.last_name}`,
      phone: profileData.phone,
      
      // Job Information
      jobTitle: profileData.job_title,
      department: profileData.department,
      hireDate: profileData.hire_date,
      employeeId: profileData.employee_id,
      
      // Organization Relationship
      organizationId: profileData.organization_id,
      organization: organizationDetails,
      
      // Access Control
      roles: profileData.roles || [],
      permissions: profileData.permissions || [],
      status: profileData.status,
      
      // Emergency Contact
      emergencyContactName: profileData.emergency_contact_name,
      emergencyContactPhone: profileData.emergency_contact_phone,
      emergencyContactRelationship: profileData.emergency_contact_relationship,
      
      // Profile Settings
      profilePictureUrl: profileData.profile_picture_url,
      timezone: profileData.timezone,
      language: profileData.language,
      
      // Activity Tracking
      lastLogin: profileData.last_login,
      loginCount: profileData.login_count,
      projectCount: profileData.project_count,
      
      // System Metadata
      isActive: profileData.is_active,
      emailVerified: profileData.email_verified,
      twoFactorEnabled: profileData.two_factor_enabled,
      createdAt: profileData.created_at,
      updatedAt: profileData.updated_at,
      createdBy: profileData.created_by
    };
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshTokens(): Promise<AuthTokens> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const config = getConfig();
    const keycloakTokenUrl = getKeycloakUrl(`/realms/${config.keycloak.realm}/protocol/openid-connect/token`);
    
    const formData = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.keycloak.clientId,
      refresh_token: refreshToken
    });

    const response = await fetch(keycloakTokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: formData
    });

    if (!response.ok) {
      // Refresh token is invalid or expired
      this.clearTokens();
      throw new Error('Session expired. Please login again.');
    }

    const tokens: AuthTokens = await response.json();
    this.storeTokens(tokens);
    
    return tokens;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (refreshToken) {
        // Revoke refresh token with Keycloak
        const config = getConfig();
        const keycloakLogoutUrl = getKeycloakUrl(`/realms/${config.keycloak.realm}/protocol/openid-connect/logout`);
        
        const formData = new URLSearchParams({
          client_id: config.keycloak.clientId,
          refresh_token: refreshToken
        });

        await fetch(keycloakLogoutUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData
        });
      }
    } catch (error) {
      console.warn('Error during Keycloak logout:', error);
    } finally {
      // Always clear local tokens
      this.clearTokens();
    }
  }

  /**
   * Request a password-reset email for an existing account.
   * Backend generates a short-lived token and emails {frontend_url}/reset-password?token=TOKEN.
   * Always resolves (200) so we don't leak whether an email is registered.
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const endpoint = buildUrl('backend', '/api/v1/auth/forgot-password');
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email, frontend_url: window.location.origin })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw { error: err.error || 'request_failed', message: err.message || 'Failed to send reset email.' };
    }
    return response.json();
  }

  /**
   * Set password for a newly-activated account.
   * Token comes from the email-activation validate response (set_password_token).
   * On success the backend returns full auth tokens so the user lands on the dashboard.
   */
  async setPassword(token: string, password: string): Promise<AuthTokens> {
    const endpoint = buildUrl('backend', '/api/v1/auth/set-password');
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token, password })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw { error: err.error || 'set_password_failed', message: err.message || 'Failed to set password.' };
    }
    const tokens: AuthTokens = await response.json();
    this.storeTokens(tokens);
    // Signal AuthProvider to re-hydrate user state from the newly stored tokens
    window.dispatchEvent(new Event('auth:tokens-stored'));
    return tokens;
  }

  /**
   * Reset password using the token received via the forgot-password email link.
   * On success the backend returns { message } and the user is sent to login.
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const endpoint = buildUrl('backend', '/api/v1/auth/reset-password');
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token, password })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw { error: err.error || 'reset_failed', message: err.message || 'Failed to reset password.' };
    }
    return response.json();
  }

  /**
   * Get user profile (wrapper around fetchUserProfile for external use)
   */
  async getUserProfile(): Promise<UserProfile> {
    return this.fetchUserProfile();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    
    if (!accessToken) return false;
    
    // Check if token is expired
    if (this.tokenExpiryTime && Date.now() / 1000 >= this.tokenExpiryTime) {
      return false;
    }
    
    return true;
  }

  /**
   * Check if token needs refresh
   */
  needsTokenRefresh(): boolean {
    if (!this.tokenExpiryTime) return false;
    return shouldRefreshToken(this.tokenExpiryTime);
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    const keys = getStorageKeys();
    return localStorage.getItem(keys.accessToken);
  }

  /**
   * Get current refresh token
   */
  private getRefreshToken(): string | null {
    const keys = getStorageKeys();
    return localStorage.getItem(keys.refreshToken);
  }

  /**
   * Store tokens securely
   */
  private storeTokens(tokens: AuthTokens): void {
    const keys = getStorageKeys();

    localStorage.setItem(keys.accessToken, tokens.access_token);
    localStorage.setItem(keys.refreshToken, tokens.refresh_token);

    // Store expiry times both in memory and localStorage
    const now = Date.now() / 1000;
    this.tokenExpiryTime = now + tokens.expires_in;
    this.refreshTokenExpiryTime = now + tokens.refresh_expires_in;

    localStorage.setItem(`${keys.accessToken}_expiry`, this.tokenExpiryTime.toString());
    localStorage.setItem(`${keys.refreshToken}_expiry`, this.refreshTokenExpiryTime.toString());
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    const keys = getStorageKeys();

    localStorage.removeItem(keys.accessToken);
    localStorage.removeItem(keys.refreshToken);
    localStorage.removeItem(`${keys.accessToken}_expiry`);
    localStorage.removeItem(`${keys.refreshToken}_expiry`);

    this.tokenExpiryTime = null;
    this.refreshTokenExpiryTime = null;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: any): AuthError {
    if (error.error) {
      // Keycloak error format
      return {
        error: error.error,
        error_description: error.error_description,
        message: this.getErrorMessage(error.error, error.error_description)
      };
    }
    
    // Generic error
    return {
      error: 'authentication_failed',
      message: error.message || 'Authentication failed. Please try again.'
    };
  }

  /**
   * Get user-friendly error messages
   */
  private getErrorMessage(error: string, description?: string): string {
    const errorMessages: { [key: string]: string } = {
      'invalid_grant': 'Invalid username or password. Please check your credentials and try again.',
      'invalid_client': 'Application configuration error. Please contact support.',
      'unauthorized_client': 'Application not authorized. Please contact support.',
      'unsupported_grant_type': 'Authentication method not supported. Please contact support.',
      'invalid_scope': 'Invalid permissions requested. Please contact support.',
      'server_error': 'Authentication server error. Please try again later.',
      'temporarily_unavailable': 'Authentication service temporarily unavailable. Please try again later.',
      'too_many_requests': 'Too many login attempts. Please wait before trying again.'
    };
    
    return errorMessages[error] || description || 'Authentication failed. Please try again.';
  }

  /**
   * Get client identifier for rate limiting
   */
  private getClientIdentifier(): string {
    // Use a combination of IP-like identifier and user agent
    const userAgent = navigator.userAgent;
    const screenInfo = `${screen.width}x${screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a simple hash-like identifier
    const identifier = `${userAgent}-${screenInfo}-${timezone}`;
    return btoa(identifier).substring(0, 16); // Base64 encoded, truncated
  }
}

// Export singleton instance
export const authService = new AuthService();